import { apiClient } from './apiService';
import { PurchaseType } from '@/enums/purchase-type';
import {
  BRANCH_POS_DEVICE_DETAIL,
  BRANCH_POS_DEVICE_FISCAL_PARAMETERS,
  BRANCH_POS_DEVICE_HEALTH,
  BRANCH_POS_DEVICE_PAIR,
  BRANCH_POS_DEVICE_PAIR_CHECK,
  BRANCH_POS_DEVICES,
  BRANCH_POS_INPOS_DISCOVER_MATCHES,
  BRANCH_POS_PROVIDER_DETAIL,
  BRANCH_POS_PROVIDERS,
  POS_PROVIDERS,
  POS_RECONCILIATION,
  POS_TRANSACTION_CANCEL,
  POS_TRANSACTION_DETAIL,
  POS_TRANSACTION_OFF_DEVICE_REFUND,
  POS_TRANSACTION_REFUND,
  POS_TRANSACTIONS
} from './endpoints';

export type PosConnectionType =
  | 'CLOUD_API'
  | 'LOCAL_BRIDGE'
  | 'TCP_IP'
  | 'ANDROID_SDK'
  | 'ANDROID_INTENT'
  | 'WEBHOOK'
  | 'POLLING'
  | 'MANUAL';

export type PosProviderAdapterKey =
  | 'MOCK'
  | 'PAVO'
  | 'INGENICO'
  | 'PAX'
  | 'PAYCELL'
  | 'PARAM'
  | 'PROVIDER_X'
  | 'INPOS'
  | 'TOKEN';

/**
 * User-facing labels for POS adapter keys. The backend key is a technical detail;
 * merchants only know brands — in particular TOKEN must always render as "Beko".
 */
export const POS_ADAPTER_LABELS: Record<PosProviderAdapterKey, string> = {
  MOCK: 'Test (Mock)',
  PAVO: 'Pavo',
  INGENICO: 'Ingenico',
  PAX: 'PAX',
  PAYCELL: 'Paycell',
  PARAM: 'Param',
  PROVIDER_X: 'Diğer',
  INPOS: 'InPOS',
  TOKEN: 'Beko'
};

/** Friendly label for an adapter key, falling back to the raw key if unknown. */
export function posAdapterLabel(key: PosProviderAdapterKey | string): string {
  return POS_ADAPTER_LABELS[key as PosProviderAdapterKey] ?? key;
}

export interface PosProvider {
  id: number;
  code: string;
  name: string;
  adapterKey: PosProviderAdapterKey;
  connectionType: PosConnectionType;
  capabilities: string[];
  configSchema?: Record<string, unknown> | null;
  isActive: boolean;
}

export interface BranchPosProvider {
  id: number;
  branchId: number;
  providerId: number;
  displayName: string;
  isDefault: boolean;
  isActive: boolean;
  settings?: Record<string, unknown> | null;
  provider: PosProvider;
}

export interface PosDevice {
  id: number;
  branchId: number;
  branchPosProviderId: number;
  providerId: number;
  label: string;
  terminalId?: string | null;
  serialNumber?: string | null;
  connectionType: PosConnectionType;
  bridgeId?: string | null;
  isDefault: boolean;
  isActive: boolean;
  lastSeenAt?: string | null;
  metadata?: Record<string, unknown> | null;
  provider?: PosProvider;
  branchPosProvider?: BranchPosProvider;
}

export type PosTransactionStatus =
  | 'PENDING'
  | 'WAITING_FOR_TERMINAL'
  | 'LOCKED_BY_TERMINAL'
  | 'APPROVED'
  | 'APPROVED_NOT_FINALIZED'
  | 'FAILED'
  | 'CANCELLED'
  | 'TIMEOUT'
  | 'UNKNOWN'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED'
  | 'MANUAL_OVERRIDE';

export type PosTransactionTargetType =
  | 'TABLE_CHECK'
  | 'SPLIT_CHECK'
  | 'PARTIAL_PAYMENT'
  | 'ORDER'
  | 'CART_ORDER'
  | 'MEMBERSHIP_SALE'
  | 'RECEIVABLE_COLLECTION';

/**
 * One tender observed on the POS device for a sale. `amount` is in TL.
 * `type` is the device vocabulary: cash | card | meal | open_account.
 */
export interface PosPaymentBreakdownLeg {
  type: string;
  amount: number;
  operatorId?: number;
}

export interface PosTransaction {
  recoveryStatus?: 'PENDING' | 'MANUAL_REVIEW' | 'COMPLETED' | null;
  recoveryKind?: 'RECEIPT_VOID' | 'REFUND' | null;
  recoveryError?: string | null;
  id: number;
  branchId: number;
  amount: string | number;
  currency: string;
  status: PosTransactionStatus;
  targetType: PosTransactionTargetType;
  targetId: number;
  providerTransactionId?: string | null;
  providerReference?: string | null;
  /**
   * Provider-native status string. For Pavo APPROVED transactions this is
   * the device's SaleNumber — the identifier Pavo/vendor support asks for
   * when troubleshooting. See pavo-pos.adapter.ts mapCheckLink.
   */
  providerStatus?: string | null;
  providerErrorCode?: string | null;
  providerErrorMessage?: string | null;
  /**
   * Loosely typed backend JSON. Read it through the accessors in
   * utils/posFiscalMetadata (fiscalDocument, invoiceCustomer,
   * receivableCustomerMissing) instead of casting inline.
   */
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  approvedAt?: string | null;
  finalizedAt?: string | null;
  /**
   * How the sale was tendered. Note this is NOT a statement that money was
   * collected: `OPEN_ACCOUNT` (Açık Hesap) means the ÖKC printed a fiş and the
   * customer left owing the branch, so it must never be summed into a takings
   * figure. See uyanik-backend accounting-ledger/ledger-tenders.ts.
   */
  purchaseType?: PurchaseType | null;
  /** Per-tender split observed from the device when available. */
  paymentBreakdown?: PosPaymentBreakdownLeg[] | null;
  device?: PosDevice;
  provider?: PosProvider;
  refunds?: PosRefund[];
}

export interface PosTransactionListResult {
  items: PosTransaction[];
  total: number;
  page: number;
  limit: number;
}

/** Device-reported KDV department/section (Excel Listing T29). */
export interface FiscalParameterSection {
  sectionNo: number;
  /** Percent ×100 as the device reports it (1000 = %10). */
  taxPercent: number;
  name?: string;
  /** Per-section sale limit in kuruş (0 / missing = no limit reported). */
  limit?: number;
  status?: number;
  type?: number;
}

/** Device parameters response (sections plus device level fiş / e-Belge info). */
export interface FiscalParameters {
  sections: FiscalParameterSection[];
  /** Per-receipt limit in KURUŞ (1200000 = 12.000,00 TL). */
  receiptLimit?: number;
  terminal?: string;
  /** 1 = e-Belge active. */
  eDocumentStatus?: number;
  integratorID?: number;
}

/**
 * Result of a day-end reconciliation run. This is an audit of OUR OWN
 * PosTransaction records for the branch/date, not a cross-check against the
 * provider's own settlement report — no POS provider we integrate with
 * exposes one over the integration path our backend uses.
 */
export interface PosReconciliationReport {
  branchId: number;
  businessDate: string;
  branchPosProviderId?: number;
  totalCount: number;
  approvedCount: number;
  approvedTotal: string;
  unresolvedCount: number;
  unresolved: PosTransaction[];
}

export type PosRefundStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'FAILED'
  | 'CANCELLED'
  | 'UNKNOWN';

export interface PosRefund {
  id: number;
  posTransactionId: number;
  amount: string | number;
  status: PosRefundStatus;
  reason?: string | null;
  idempotencyKey?: string;
  /** 'OFF_DEVICE' for a refund recorded with the off device form. */
  providerStatus?: string | null;
  providerReference?: string | null;
  providerErrorCode?: string | null;
  providerErrorMessage?: string | null;
  requestedByUserId?: number;
  approvedAt?: string | null;
  failedAt?: string | null;
  createdAt: string;
}

/** Tender of a sale leg, device vocabulary. */
export type OffDeviceRefundLegType = 'cash' | 'card' | 'meal' | 'open_account';

/** How one leg went back: OPEN_ACCOUNT_VOID voids the Açık Hesap receivable. */
export type OffDeviceRefundMethod =
  | 'CASH'
  | 'BANK_APP'
  | 'MEAL_PROVIDER'
  | 'OPEN_ACCOUNT_VOID';

export interface OffDeviceRefundLeg {
  type: OffDeviceRefundLegType;
  method: OffDeviceRefundMethod;
}

export interface OffDeviceRefundPayload {
  /** 8 to 500 characters. */
  reason: string;
  /** Token: optional (derived on the server). InPOS: exactly one. */
  legs?: OffDeviceRefundLeg[];
  externalReference?: string;
  /** InPOS only. */
  deviceVoidPrinted?: boolean;
}

export interface OffDeviceRefundResult {
  refund: PosRefund;
  transaction: {
    id: number;
    status: PosTransactionStatus;
    recoveryStatus: 'PENDING' | 'MANUAL_REVIEW' | 'COMPLETED' | null;
    recoveryError: string | null;
  };
}

const branchPosProvidersUrl = (branchId: number) =>
  BRANCH_POS_PROVIDERS.replace(':branchId', String(branchId));

const branchPosProviderDetailUrl = (
  branchId: number,
  branchProviderId: number
) =>
  BRANCH_POS_PROVIDER_DETAIL.replace(':branchId', String(branchId)).replace(
    ':branchProviderId',
    String(branchProviderId)
  );

const branchPosDevicesUrl = (branchId: number) =>
  BRANCH_POS_DEVICES.replace(':branchId', String(branchId));

const branchPosDeviceDetailUrl = (branchId: number, deviceId: number) =>
  BRANCH_POS_DEVICE_DETAIL.replace(':branchId', String(branchId)).replace(
    ':deviceId',
    String(deviceId)
  );

export const posService = {
  async listProviders() {
    const response = await apiClient.get<PosProvider[]>(POS_PROVIDERS);
    return response.data;
  },

  async listBranchProviders(branchId: number) {
    const response = await apiClient.get<BranchPosProvider[]>(
      branchPosProvidersUrl(branchId)
    );
    return response.data;
  },

  async createBranchProvider(
    branchId: number,
    data: {
      providerId: number;
      /**
       * Optional — backend falls back to the provider's catalog name (e.g.
       * "Pavo"). Only send when a branch legitimately wants a custom label.
       */
      displayName?: string;
      isDefault?: boolean;
      credentials?: Record<string, unknown>;
      settings?: Record<string, unknown>;
    }
  ) {
    const response = await apiClient.post<BranchPosProvider>(
      branchPosProvidersUrl(branchId),
      data
    );
    return response.data;
  },

  /**
   * `settings` REPLACES the stored settings object wholesale (backend does not
   * merge) — callers must spread the existing settings before overriding one
   * key, or the other stored values (e.g. merchantUid, tokenBranchId) are lost.
   */
  async updateBranchProvider(
    branchId: number,
    branchProviderId: number,
    data: {
      displayName?: string;
      isDefault?: boolean;
      isActive?: boolean;
      credentials?: Record<string, unknown>;
      settings?: Record<string, unknown>;
    }
  ) {
    const response = await apiClient.patch<BranchPosProvider>(
      branchPosProviderDetailUrl(branchId, branchProviderId),
      data
    );
    return response.data;
  },

  async deleteBranchProvider(branchId: number, branchProviderId: number) {
    await apiClient.delete(
      branchPosProviderDetailUrl(branchId, branchProviderId)
    );
  },

  async listDevices(branchId: number) {
    const response = await apiClient.get<PosDevice[]>(
      branchPosDevicesUrl(branchId)
    );
    return response.data;
  },

  async createDevice(
    branchId: number,
    data: {
      branchPosProviderId: number;
      label: string;
      terminalId?: string;
      serialNumber?: string;
      /**
       * Optional — inherited from the branch's provider (all real providers
       * are CLOUD_API). Kept for scripts/tests that need to override.
       */
      connectionType?: PosConnectionType;
      isDefault?: boolean;
      metadata?: Record<string, unknown>;
    }
  ) {
    const response = await apiClient.post<PosDevice>(
      branchPosDevicesUrl(branchId),
      data
    );
    return response.data;
  },

  async deleteDevice(branchId: number, deviceId: number) {
    await apiClient.delete(branchPosDeviceDetailUrl(branchId, deviceId));
  },

  async healthCheck(branchId: number, deviceId: number) {
    const url = BRANCH_POS_DEVICE_HEALTH.replace(
      ':branchId',
      String(branchId)
    ).replace(':deviceId', String(deviceId));
    const response = await apiClient.post<{
      healthy: boolean;
      providerStatus?: string;
      errorMessage?: string;
    }>(url);
    return response.data;
  },

  async startDevicePairing(branchId: number, deviceId: number) {
    const url = BRANCH_POS_DEVICE_PAIR.replace(
      ':branchId',
      String(branchId)
    ).replace(':deviceId', String(deviceId));
    const response = await apiClient.post<{
      pairingId?: string | null;
      pairingCode?: string | null;
      isApproved: boolean;
      approveTime?: string | null;
      targetFingerprint?: string | null;
    }>(url);
    return response.data;
  },

  async checkDevicePairing(
    branchId: number,
    deviceId: number,
    pairingId: string
  ) {
    const url = BRANCH_POS_DEVICE_PAIR_CHECK.replace(
      ':branchId',
      String(branchId)
    ).replace(':deviceId', String(deviceId));
    const response = await apiClient.post<{
      pairingId?: string | null;
      pairingCode?: string | null;
      isApproved: boolean;
      approveTime?: string | null;
      targetFingerprint?: string | null;
    }>(url, { pairingId });
    return response.data;
  },

  // Token webhook registration (Set Client Settings) is intentionally not
  // callable from the admin panel — it registers itself on provider save and
  // is otherwise an ops concern. The backend endpoints still exist for ops;
  // see BranchPosSettingsManager for why no UI surfaces them.

  async discoverInposMatches(branchId: number, vkn: string) {
    const url = BRANCH_POS_INPOS_DISCOVER_MATCHES.replace(
      ':branchId',
      String(branchId)
    );
    const response = await apiClient.get<{
      matches: Array<{
        matchId: string;
        name?: string;
        raw: Record<string, unknown>;
      }>;
    }>(url, { params: { vkn } });
    return response.data;
  },

  /**
   * Device-reported KDV department/section list (Excel Listing T29) plus the
   * receipt limit and e-Belge status. Only providers whose adapter supports it
   * (Token) return data — others 400. `refresh: true` bypasses the server's
   * 24h cache (`?refresh=true`).
   */
  async getFiscalParameters(
    branchId: number,
    deviceId: number,
    options?: { refresh?: boolean }
  ) {
    const url = BRANCH_POS_DEVICE_FISCAL_PARAMETERS.replace(
      ':branchId',
      String(branchId)
    ).replace(':deviceId', String(deviceId));
    const response = options?.refresh
      ? await apiClient.get<FiscalParameters>(url, { refresh: true })
      : await apiClient.get<FiscalParameters>(url);
    return response.data;
  },

  /**
   * List PosTransactions with server-side filters + pagination. `branchId` is
   * optional (SUPER_ADMIN-only cross-tenant view — non-superadmins must pass
   * it and the backend enforces that). Extra filters (companyId, providerId,
   * date range, search) are SUPER_ADMIN-only regardless of branchId.
   *
   * Response items include the top-level `branch.company` shape for
   * cross-branch views — see PosTransactionWithBranch.
   */
  async listTransactions(params: {
    branchId?: number;
    companyId?: number;
    providerId?: number;
    status?: PosTransactionStatus;
    targetType?: string;
    targetId?: number;
    createdAtFrom?: string;
    createdAtTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await apiClient.get<PosTransactionListResult>(
      POS_TRANSACTIONS,
      params
    );
    return response.data;
  },

  async getTransaction(transactionId: number) {
    const response = await apiClient.get<PosTransaction>(
      POS_TRANSACTION_DETAIL.replace(':id', String(transactionId))
    );
    return response.data;
  },

  async cancelTransaction(transactionId: number) {
    const response = await apiClient.post<PosTransaction>(
      POS_TRANSACTION_CANCEL.replace(':id', String(transactionId))
    );
    return response.data;
  },

  async refundTransaction(
    transactionId: number,
    data: { amount: number; reason?: string }
  ) {
    const response = await apiClient.post<PosRefund>(
      POS_TRANSACTION_REFUND.replace(':id', String(transactionId)),
      data
    );
    return response.data;
  },

  /**
   * Token / InPOS: the sale was refunded outside the device. The server books
   * the full amount and reverses the sale (order, stock, points, check,
   * receivable) in one step; it answers 4xx when that cannot be proven.
   */
  async recordOffDeviceRefund(
    transactionId: number,
    data: OffDeviceRefundPayload
  ) {
    const response = await apiClient.post<OffDeviceRefundResult>(
      POS_TRANSACTION_OFF_DEVICE_REFUND.replace(':id', String(transactionId)),
      data
    );
    return response.data;
  },

  async reconcile(data: {
    branchId: number;
    businessDate: string;
    branchPosProviderId?: number;
  }) {
    const response = await apiClient.post<PosReconciliationReport>(
      POS_RECONCILIATION,
      data
    );
    return response.data;
  }
};
